import { Alert, Avatar, Button, Flex, Select, Typography } from "antd";
import { AvailabilitySlots } from "components/AvailabilitySlots";
import { StyledNotification } from "components/AvailabilitySlots/styles";
import { Loader } from "components/Loader";
import { StartContent } from "components/StartContent";
import { SummaryTitle } from "components/StartContent/styled";
import { RESERVATION_OPTIONS, TRACKING } from "config/consts";
import { API_URL } from "config/api";
import { useApp } from "contexts/AppProvider";
import dayjs from "dayjs";
import { LOCALES } from "i18n/messages";
import {
  MAX_AVAILABILITY_MONTH_RETRIES,
  useGetAvailabilityQuery,
  useGetBookingUsersQuery,
  useGetPersonalDataQuery,
} from "queries/reservation.query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { getAvailabilityParam } from "repositories/reservations/reservationRepository.param";
import { ISingleAvailability } from "types";
import { getDefaultVisitTypeOption } from "lib/reservationOptions";
import { getBookingFilterMessages } from "i18n/bookingFilters";
import { uniqueOptionIds } from "lib/optionIds";
import { ContinueTransaction } from "./ContinueTransaction";

import { BookingFilters } from "components/BookingFilters";
import {
  getBookingBranchIds,
  getBookingCities,
  getBookingTherapistIds,
  selectBookingCity,
  selectBookingUser,
  getLegacyBookingCity,
} from "lib/bookingUsers";

export const Start = () => {
  const location = useLocation();
  const {
    branches,
    setReservationPayload,
    dispatchReservationPayload,
    reservationPayload,
    services,
    customerId,
    activeTimeslot,
    setActiveTimeslot,
    price,
    getBranchById,
    activeService,
    setActiveService,
    setActiveStep,
    sentEvents,
    currency,
  } = useApp();
  const [isChild, setIsChild] = useState<boolean>(false);
  const [birthYear, setBirthYear] = useState<string | null>(reservationPayload.birthYear ?? null);
  const navigate = useNavigate();

  const [timeSchedules, setTimeSchedules] = useState<ISingleAvailability[]>([]);
  const [showSelect, setShowSelect] = useState<boolean>(false);
  const [loadedDays, setLoadedDays] = useState<number>(30);
  const [availabilityAnchorDate, setAvailabilityAnchorDate] = useState<string | null>(null);
  const [availabilityState, setAvailabilityState] = useState<{ key: string; rows: ISingleAvailability[] }>({
    key: "",
    rows: [],
  });
  const [retries, setRetries] = useState<number>(0);

  const { locale } = useIntl();
  const { formatMessage } = useIntl();
  const catalog = useGetBookingUsersQuery(customerId);
  const selectedUserId = reservationPayload.selectedUserId || "";
  const specialistServices = useGetPersonalDataQuery(customerId, selectedUserId);
  const visibleServices = useMemo(
    () =>
      selectedUserId
        ? (specialistServices.data?.services ?? []).filter((service) => service.publicVisible !== false)
        : services,
    [selectedUserId, specialistServices.data?.services, services],
  );
  const selectedService = visibleServices?.find((service) => service.id === reservationPayload.serviceId);
  const selectedVisitType = selectedService?.options.find(
    (option) =>
      option.id === reservationPayload.visitTypeId &&
      option.category === RESERVATION_OPTIONS.VISIT_TYPE &&
      option.publicVisible !== false,
  );
  const specialistServiceReady =
    !selectedUserId ||
    Boolean(specialistServices.data && selectedService && selectedVisitType && activeService === selectedService);
  const copy = getBookingFilterMessages(locale);
  const selectedUser = catalog.data?.find((user) => user.userId === selectedUserId);
  const selectedCity = reservationPayload.selectedCity ?? getLegacyBookingCity(branches, reservationPayload.branchIds);
  const searchBranchIds = getBookingBranchIds(branches, selectedCity);
  const selectedTherapistIds = getBookingTherapistIds(selectedUser, searchBranchIds);
  const relevantAssignments = (specialistServices.data?.therapists ?? []).filter(({ id }) =>
    selectedTherapistIds.includes(id),
  );
  const doesNotAcceptNewPatients =
    !!selectedUserId &&
    relevantAssignments.length > 0 &&
    relevantAssignments.every(({ acceptsNewPatients }) => acceptsNewPatients === false);
  const availableUsers = (catalog.data ?? []).filter(
    (user) => !selectedCity || getBookingTherapistIds(user, searchBranchIds).length > 0,
  );
  const availableBranches = selectedUserId
    ? branches.filter((branch) => selectedUser?.therapists.some((assignment) => assignment.branchId === branch.id))
    : branches;
  const filterKey = JSON.stringify({
    customerId,
    serviceId: reservationPayload.serviceId,
    optionIds: reservationPayload.optionIds,
    city: selectedCity,
    branchIds: searchBranchIds,
    online: reservationPayload.isOnline,
    selectedUserId,
    therapistIds: selectedTherapistIds,
    doesNotAcceptNewPatients,
    birthYear: isChild ? birthYear : null,
  });
  const accumulatedAvailability = availabilityState.key === filterKey ? availabilityState.rows : [];
  const setAccumulatedAvailability = (
    next: ISingleAvailability[] | ((rows: ISingleAvailability[]) => ISingleAvailability[]),
  ) =>
    setAvailabilityState((prev) => ({
      key: filterKey,
      rows: typeof next === "function" ? next(prev.key === filterKey ? prev.rows : []) : next,
    }));

  // Apply the same defaults as the personal flow, retaining a supported
  // service/visit type when returning from the form or changing specialist.
  useEffect(() => {
    if (selectedUserId && !specialistServices.data) return;
    const persistedService = visibleServices?.find((service) => service.id === reservationPayload.serviceId);
    const defaultService = persistedService ?? (selectedUserId ? visibleServices[0] : undefined);
    if (!defaultService) return;
    const persistedVisitType = defaultService.options.find(
      (option) =>
        option.id === reservationPayload.visitTypeId &&
        option.category === RESERVATION_OPTIONS.VISIT_TYPE &&
        option.publicVisible !== false,
    );
    const visitType = persistedVisitType ?? (selectedUserId ? getDefaultVisitTypeOption(defaultService) : undefined);
    const resetService = activeService !== defaultService || reservationPayload.serviceId !== defaultService.id;
    if (resetService) {
      setActiveService(defaultService);
      if (selectedUserId)
        dispatchReservationPayload({
          type: "setService",
          serviceId: defaultService.id,
          prevActiveService: activeService,
          newActiveService: defaultService,
          locale,
        });
    }
    if (selectedUserId && visitType && (resetService || !persistedVisitType)) {
      dispatchReservationPayload({ type: "setVisitType", optionId: visitType.id, activeService: defaultService });
    }
    const child = ["Dziecko", "Dziecko / nastolatek"].includes(visitType?.name ?? "");
    setIsChild(child);
    setBirthYear(child ? (reservationPayload.birthYear ?? null) : null);
    if (selectedUserId && !child && reservationPayload.birthYear) {
      setReservationPayload((prev) => ({ ...prev, birthYear: undefined }));
    }
  }, [
    selectedUserId,
    specialistServices.data,
    visibleServices,
    activeService,
    locale,
    reservationPayload.serviceId,
    reservationPayload.visitTypeId,
    reservationPayload.birthYear,
    setActiveService,
    setReservationPayload,
    dispatchReservationPayload,
  ]);

  // Persist the city independently of assignments so clearing a specialist
  // broadens back to every branch in the selected city. Also restore legacy state.
  useEffect(() => {
    if (!branches.length) return;
    setReservationPayload((prev) => {
      const city = prev.selectedCity ?? getLegacyBookingCity(branches, prev.branchIds);
      const branchIds = getBookingBranchIds(branches, city);
      if (prev.selectedCity === city && JSON.stringify(prev.branchIds) === JSON.stringify(branchIds)) return prev;
      return { ...prev, selectedCity: city, branchIds };
    });
  }, [branches, setReservationPayload]);

  const readyToFetch = Boolean(
    !doesNotAcceptNewPatients &&
      specialistServiceReady &&
      reservationPayload.visitTypeId &&
      reservationPayload.serviceId &&
      (selectedCity || reservationPayload.isOnline || selectedUserId) &&
      (!selectedCity || searchBranchIds.length > 0) &&
      (!isChild || birthYear) &&
      (!selectedUserId || selectedTherapistIds.length > 0),
  );
  const availabilityPayload = useMemo<getAvailabilityParam>(
    () => ({
      customerId,
      serviceIds: [reservationPayload.serviceId],
      branchIds: searchBranchIds,
      ...(selectedUserId ? { therapistIds: selectedTherapistIds } : {}),
      optionIds: uniqueOptionIds(reservationPayload.optionIds),
      childBirthYear: isChild ? birthYear : null,
      dateFrom: dayjs().format("YYYY-MM-DD"),
      dateTo: dayjs().add(loadedDays, "days").format("YYYY-MM-DD"),
      includeOnline: reservationPayload.isOnline,
      // filterKey includes every search filter, including resolved assignments.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [filterKey, loadedDays],
  );

  const {
    data: availability,
    isLoading,
    isFetching,
    isFetched,
    isPlaceholderData,
    isError: availabilityError,
    refetch: retryAvailability,
  } = useGetAvailabilityQuery(availabilityPayload, readyToFetch);

  const clearSearchSelection = () => {
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setAvailabilityAnchorDate(null);
  };

  const handleBranchChange = (city: string) => {
    setReservationPayload((prev) => selectBookingCity(prev, city, branches, catalog.data));
    clearSearchSelection();
  };

  const handleOnlineChange = (isOnline: boolean) => {
    const onlineOptionId = activeService?.options.find(
      (option) => option.category === RESERVATION_OPTIONS.LOCATION && option.publicVisible !== false,
    )?.id;
    dispatchReservationPayload({ type: "setIsOnline", isOnline, onlineOptionId });
    clearSearchSelection();
  };

  const handleServiceChange = (value) => {
    const newService = visibleServices.find((el) => el.id === value);

    if (!newService) {
      console.error("Service not found");
      return;
    }

    // Detect child option via translation match before dispatching, so we can
    // mirror the legacy isChild side-effect (used by birthYear visibility).
    const childOptionInNewService = activeService
      ? reservationPayload.optionIds
          .map((optionId) => activeService.options.find((el) => el.id === optionId)?.translations[locale])
          .filter(Boolean)
          .map((translation) => newService.options.find((el) => el.translations[locale] === translation))
          .find((option) => ["Dziecko", "Dziecko / nastolatek"].includes(option?.name ?? ""))
      : undefined;
    const child = !!childOptionInNewService;

    if (child !== isChild) {
      setIsChild(child);
    }

    dispatchReservationPayload({
      type: "setService",
      serviceId: value,
      prevActiveService: activeService,
      newActiveService: newService,
      locale,
    });

    setActiveService(newService);
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setAvailabilityAnchorDate(null);
  };

  const handleOptionChange = (value) => {
    const optionName = activeService?.options.find((el) => el.id === value)?.name;
    const child = ["Dziecko", "Dziecko / nastolatek"].includes(optionName ?? "");

    setIsChild(child);

    if (!child) {
      setBirthYear(null);
    }

    dispatchReservationPayload({ type: "setVisitType", optionId: value, activeService });

    // Use the functional updater so we don't clobber the visitTypeId/optionIds
    // just set by dispatchReservationPayload above (the closure-captured
    // reservationPayload is stale and would overwrite the reducer's update).
    setReservationPayload((prev) => ({
      ...prev,
      birthYear: child ? prev.birthYear : null,
    }));
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setAvailabilityAnchorDate(null);
  };

  const handleNextClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/form${location.search}`);
  };

  const handleBirthYear = (value: string) => {
    setBirthYear(value);
    setReservationPayload({
      ...reservationPayload,
      birthYear: value,
    });
    setAccumulatedAvailability([]);
  };

  const handleLoadMore = () => {
    const newDays = loadedDays + 30;
    setLoadedDays(newDays);
  };

  useEffect(() => {
    if (
      readyToFetch &&
      !isPlaceholderData &&
      !isFetching &&
      availability &&
      availability.length === 0 &&
      retries < MAX_AVAILABILITY_MONTH_RETRIES
    ) {
      setRetries((prev) => prev + 1);
      handleLoadMore();
    }
  }, [availability, readyToFetch, isPlaceholderData, isFetching]);

  useEffect(() => {
    if (readyToFetch && !isPlaceholderData && availability && availability.length > 0) {
      setAccumulatedAvailability((prev) => {
        const prevIds = new Set(prev.map((el) => `${el.scheduleId}_${el.date}_${el.startTime}`));
        const newItems = availability.filter((el) => !prevIds.has(`${el.scheduleId}_${el.date}_${el.startTime}`));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
    }
  }, [availability, isPlaceholderData, readyToFetch, filterKey]);

  useEffect(() => {
    if (
      (reservationPayload.serviceId || reservationPayload.isOnline || reservationPayload.branchIds.length > 0) &&
      !sentEvents.includes(TRACKING.OPEN)
    ) {
      setActiveStep(TRACKING.OPEN);
    }
  }, [
    reservationPayload.serviceId,
    reservationPayload.isOnline,
    reservationPayload.branchIds,
    sentEvents,
    setActiveStep,
  ]);

  useEffect(() => {
    if (isFetched) {
      setActiveStep(TRACKING.SEARCH);
    }
  }, [isFetched, setActiveStep]);

  useEffect(() => {
    if (activeService && locale === LOCALES.ENGLISH) {
      const lang = activeService.options.find(
        (el) => el.category === RESERVATION_OPTIONS.LANGUGAGE && el.publicVisible !== false,
      );
      if (lang) {
        dispatchReservationPayload({ type: "setLanguageOption", optionId: lang.id, enabled: true });
      }
    }
  }, [activeService, locale, dispatchReservationPayload]);

  useEffect(() => {
    if (showSelect && timeSchedules.length === 1) {
      setShowSelect(false);
    }
  }, [timeSchedules, showSelect]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const branchName = queryParams.get("branch");
    const activeServiceId = queryParams.get("service");

    if (!selectedUserId && services && activeServiceId) {
      const service = services.find((el) => el.id === activeServiceId);
      if (service && reservationPayload.serviceId !== activeServiceId) {
        handleServiceChange(activeServiceId);
      }
    }

    if (branches && branchName) {
      const b = branches.filter((el) => el.city.toLowerCase() === branchName.toLowerCase());

      if (
        b.length > 0 &&
        reservationPayload.selectedCity === undefined &&
        !reservationPayload.branchIds.length &&
        !reservationPayload.selectedUserId
      ) {
        setReservationPayload((prev) => ({ ...prev, selectedCity: b[0].city, branchIds: b.map((el) => el.id) }));
      }
    }
  }, [branches, services, location.search]);

  const previousFilterKey = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKey.current === filterKey) return;
    previousFilterKey.current = filterKey;
    setTimeSchedules([]);
    setActiveTimeslot(null);
    setAvailabilityAnchorDate(null);
    setLoadedDays(30);
    setRetries(0);
  }, [filterKey, setActiveTimeslot]);

  const handleSpecialistChange = (userId: string) => {
    setReservationPayload((prev) => selectBookingUser(prev, userId, branches, catalog.data));
    clearSearchSelection();
  };

  const branch = activeTimeslot ? getBranchById(activeTimeslot.branchId) : null;
  const childAge = 2024 - parseInt(birthYear || "2024");
  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTimeslot && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeTimeslot]);

  const [pendingPaymentLink, setPendingPaymentLink] = useState<string | null>(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentLinkParam = queryParams.get("paymentLink");

    if (!paymentLinkParam) {
      setPendingPaymentLink(null);
      return;
    }

    const paymentLink = decodeURIComponent(paymentLinkParam);

    let paymentId = "";
    try {
      const parsedUrl = new URL(paymentLink);
      const paymentParam = parsedUrl.searchParams.get("id") || "";
      paymentId = paymentParam.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    } catch {
      setPendingPaymentLink(null);
      return;
    }

    if (!paymentId) {
      setPendingPaymentLink(null);
      return;
    }

    fetch(`${API_URL}/public/payments/${paymentId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.success === true) {
          setPendingPaymentLink(paymentLink);
        } else {
          setPendingPaymentLink(null);
        }
      })
      .catch(() => {
        setPendingPaymentLink(null);
      });
  }, [location.search]);

  if (pendingPaymentLink) {
    return (
      <ContinueTransaction
        url={pendingPaymentLink}
        onDiscard={() => {
          const queryParams = new URLSearchParams(location.search);
          queryParams.delete("paymentLink");
          navigate(
            {
              pathname: location.pathname,
              search: queryParams.toString() ? `?${queryParams.toString()}` : "",
            },
            { replace: true },
          );
          setPendingPaymentLink(null);
        }}
      />
    );
  }
  return (
    <>
      {services?.length && branches?.length ? (
        <>
          <StartContent
            activeCity={selectedCity || null}
            cities={getBookingCities(availableBranches)}
            handleBranchChange={handleBranchChange}
            handleOnlineChange={handleOnlineChange}
            handleServiceChange={handleServiceChange}
            handleOptionChange={handleOptionChange}
            activeService={selectedUserId ? (selectedService ?? null) : activeService}
            services={visibleServices}
            serviceSelectionLoading={!!selectedUserId && specialistServices.isLoading}
            isChild={isChild}
            birthYear={birthYear}
            setBirthYear={handleBirthYear}
            availability={readyToFetch ? availability : undefined}
            therapists={[]}
            allowAnyLocation={!!selectedUserId}
            bookingFilters={
              <BookingFilters
                cities={getBookingCities(availableBranches)}
                users={availableUsers}
                selectedCity={selectedCity}
                selectedUserId={selectedUserId}
                selectedUserLabel={reservationPayload.therapist}
                onCityChange={handleBranchChange}
                onSpecialistChange={handleSpecialistChange}
                isLoading={catalog.isLoading}
                isError={catalog.isError}
                onRetry={() => catalog.refetch()}
              />
            }
          />
          {doesNotAcceptNewPatients && (
            <StyledNotification role="status" vertical align="center" style={{ marginTop: 20, padding: 10 }}>
              <Typography.Text>
                {formatMessage({
                  defaultMessage: "Brak wolnych terminów. Obecnie specjalista nie przyjmuje nowych pacjentów",
                  id: "TwJC9j",
                })}
              </Typography.Text>
            </StyledNotification>
          )}
          {selectedUserId && specialistServices.isError && (
            <Alert
              type="error"
              message={copy.servicesError}
              action={<Button onClick={() => specialistServices.refetch()}>{copy.retry}</Button>}
            />
          )}
          {selectedUserId && specialistServices.data && !visibleServices.length && !doesNotAcceptNewPatients && (
            <Typography.Paragraph role="status">{copy.noServices}</Typography.Paragraph>
          )}
          {selectedUserId && !catalog.isLoading && !catalog.isError && selectedTherapistIds.length === 0 && (
            <Typography.Paragraph role="status">
              {locale === "pl"
                ? "Brak dostępnych terminów wybranego specjalisty"
                : "No available appointments for the selected specialist"}
            </Typography.Paragraph>
          )}
          {readyToFetch && availabilityError && (
            <Alert
              type="error"
              message={locale === "pl" ? "Nie udało się pobrać terminów" : "Unable to load appointments"}
              action={
                <Button onClick={() => retryAvailability()}>{locale === "pl" ? "Spróbuj ponownie" : "Retry"}</Button>
              }
            />
          )}
          {readyToFetch &&
          (accumulatedAvailability.length > 0 ||
            (isFetched && !isPlaceholderData && retries === MAX_AVAILABILITY_MONTH_RETRIES)) &&
          reservationPayload.visitTypeId ? (
            <>
              {accumulatedAvailability && accumulatedAvailability.length > 0 ? (
                <div style={{ padding: 20 }}>
                  <SummaryTitle>{formatMessage({ defaultMessage: "Dostępne terminy", id: "hpEV8w" })}</SummaryTitle>
                  <AvailabilitySlots
                    duration={activeService.bookingTime}
                    availability={accumulatedAvailability}
                    loadedDays={loadedDays}
                    setTimeSchedules={setTimeSchedules}
                    onLoadMore={handleLoadMore}
                    isLoadingMore={isFetching}
                    persistedAnchorDate={availabilityAnchorDate}
                    onAnchorDateChange={setAvailabilityAnchorDate}
                  />
                  {activeTimeslot && (
                    <div ref={summaryRef} style={{ marginTop: 20 }}>
                      <SummaryTitle>{formatMessage({ defaultMessage: "Twoja wizyta u:", id: "BGY/Ks" })}</SummaryTitle>

                      <Flex vertical>
                        <div>
                          {showSelect ? (
                            <Select
                              style={{ width: "100%" }}
                              defaultValue={activeTimeslot.scheduleId}
                              options={timeSchedules
                                .sort((a, b) => {
                                  const nameA = a.therapist.user.lastName.toLowerCase();
                                  const nameB = b.therapist.user.lastName.toLowerCase();
                                  return nameA.localeCompare(nameB);
                                })
                                .map((el) => ({
                                  label: (
                                    <div>{`${el.therapist.user.firstName} ${el.therapist.user.lastName} — ${el.isOnline ? "Online" : [getBranchById(el.branchId)?.name, getBranchById(el.branchId)?.street, getBranchById(el.branchId)?.city].filter(Boolean).join(", ")}`}</div>
                                  ),
                                  value: el.scheduleId,
                                }))}
                              onChange={(value) => {
                                const selected = timeSchedules.find((el) => el.scheduleId === value);

                                setActiveTimeslot(selected);
                                setShowSelect(false);
                              }}
                            />
                          ) : (
                            <Flex>
                              {activeTimeslot.therapist.user.avatarUrl && (
                                <Avatar
                                  src={activeTimeslot.therapist.user.avatarUrl}
                                  style={{ marginRight: 10 }}
                                  size={90}
                                />
                              )}

                              <Flex
                                vertical
                                style={{
                                  width: activeTimeslot.therapist.user.avatarUrl ? "calc(100% - 90px)" : "auto",
                                }}
                              >
                                <Typography.Text strong>
                                  {activeTimeslot.therapist.user.firstName} {activeTimeslot.therapist.user.lastName}{" "}
                                  {timeSchedules.length > 1 && (
                                    <Button
                                      type="text"
                                      onClick={() => setShowSelect(true)}
                                      style={{ textDecoration: "underline", textTransform: "uppercase" }}
                                    >
                                      {formatMessage({ defaultMessage: "Zmień", id: "OH4sDW" })}
                                    </Button>
                                  )}
                                </Typography.Text>
                                {activeTimeslot.therapist.user.title && (
                                  <Typography.Text type="secondary" italic>
                                    {activeTimeslot.therapist.user.title}
                                  </Typography.Text>
                                )}
                                {activeTimeslot.therapist.user.bio && (
                                  <Typography.Text style={{ marginBottom: 20 }}>
                                    {activeTimeslot.therapist.user.bio}
                                  </Typography.Text>
                                )}
                              </Flex>
                            </Flex>
                          )}
                        </div>
                      </Flex>

                      <div>
                        <Typography.Text style={{ margin: "10px 10px 0 0", display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Data", id: "XHHR08" })}:{" "}
                        </Typography.Text>
                        <Typography.Text strong>
                          {dayjs(`${activeTimeslot.date} ${activeTimeslot.startTime}`, "YYYY-MM-DD HH:mm:ss").format(
                            "DD MMMM YYYY, HH:mm",
                          )}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text style={{ marginRight: "10px", display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Koszt wizyty", id: "qfbfoZ" })}:
                        </Typography.Text>

                        <Typography.Text strong>
                          {`${price} 
                          ${currency}`}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
                          {formatMessage({ defaultMessage: "Adres gabinetu", id: "IvLSQl" })}:
                        </Typography.Text>
                        <Typography.Text strong>
                          {activeTimeslot.isOnline
                            ? formatMessage({ defaultMessage: "Wizyta online", id: "iysARY" })
                            : `${branch?.street || ""}, ${branch?.city || ""}`}
                        </Typography.Text>
                      </div>
                      {isChild && childAge <= activeTimeslot.therapist.noChildOnFirstVisitAge && (
                        <div style={{ marginTop: 20, fontStyle: "italic" }}>
                          <Typography.Text>
                            {formatMessage({
                              defaultMessage:
                                "Uwaga: Na pierwszą wizytę zapraszamy jednego lub oboje rodziców/opiekunów",
                              id: "TJusWe",
                            })}
                          </Typography.Text>{" "}
                          <Typography.Text strong>
                            {formatMessage({
                              defaultMessage: "BEZ dziecka",
                              id: "Pj/7gf",
                            })}
                            .
                          </Typography.Text>
                        </div>
                      )}

                      <Flex justify="flex-end" style={{ width: "100%", marginTop: 20 }}>
                        <Button type="primary" onClick={handleNextClick} style={{ boxShadow: "none" }}>
                          {formatMessage({ defaultMessage: "Dalej", id: "+N4HKT" })}
                        </Button>
                      </Flex>
                    </div>
                  )}
                </div>
              ) : (
                <StyledNotification vertical align="center" style={{ marginTop: 20 }}>
                  <Typography.Text>
                    {selectedUserId
                      ? locale === "pl"
                        ? "Brak dostępnych terminów wybranego specjalisty"
                        : "No available appointments for the selected specialist"
                      : formatMessage({ defaultMessage: "Brak dostępnych terminów", id: "EDBKe2" })}
                  </Typography.Text>
                  <Typography.Text>
                    {formatMessage({
                      defaultMessage: "Spróbuj znaleźć termin w innej lokalizacji lub online",
                      id: "don3dF",
                    })}
                  </Typography.Text>
                </StyledNotification>
              )}
            </>
          ) : (
            <>
              {readyToFetch &&
                (isLoading ||
                  isPlaceholderData ||
                  (availability &&
                    availability.length === 0 &&
                    retries > 0 &&
                    retries < MAX_AVAILABILITY_MONTH_RETRIES)) && <Loader />}
            </>
          )}
        </>
      ) : (
        <Loader />
      )}
    </>
  );
};
